import { motion } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Handshake, Loader2, CheckCircle2, XCircle, Clock, ExternalLink, Phone, MapPin, Copy, Eye, EyeOff } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { logAudit } from "@/lib/auditLog";
import { useAdminOrgContext } from "@/hooks/useAdminOrgContext";
import { PARTNER_TYPE_LABELS } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const STATUS_MAP: Record<string, { label: string; className: string }> = {
  pending: { label: "Pendente", className: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  approved: { label: "Aprovado", className: "bg-green-100 text-green-800 border-green-200" },
  rejected: { label: "Rejeitado", className: "bg-red-100 text-red-800 border-red-200" },
};


interface Credentials {
  email: string;
  password: string | null;
  existingUser?: boolean;
}

function CredentialsDialog({ credentials, open, onClose }: { credentials: Credentials | null; open: boolean; onClose: () => void }) {
  const [showPassword, setShowPassword] = useState(false);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: `${label} copiado!` });
  };

  if (!credentials) return null;

  const isExisting = credentials.existingUser;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            {isExisting ? "Parceiro vinculado com sucesso!" : "Conta criada com sucesso!"}
          </DialogTitle>
          <DialogDescription>
            {isExisting
              ? "O usuário já existia e foi vinculado à nova organização parceira. Ele pode usar a senha atual."
              : "Envie essas credenciais para o parceiro. A senha é temporária."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="bg-muted/50 rounded-xl p-4 space-y-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">E-mail</p>
              <div className="flex items-center gap-2">
                <code className="text-sm font-mono flex-1 break-all">{credentials.email}</code>
                <Button size="icon" variant="ghost" className="shrink-0" onClick={() => copyToClipboard(credentials.email, "E-mail")}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {credentials.password && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Senha temporária</p>
                <div className="flex items-center gap-2">
                  <code className="text-sm font-mono flex-1 break-all">
                    {showPassword ? credentials.password : "••••••••••••"}
                  </code>
                  <Button size="icon" variant="ghost" className="shrink-0" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button size="icon" variant="ghost" className="shrink-0" onClick={() => copyToClipboard(credentials.password!, "Senha")}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
          {credentials.password && (
            <div className="flex gap-2">
              <Button
                className="flex-1"
                onClick={() => {
                  const msg = `Olá! Sua conta no Corre Digital foi criada.\n\nAcesse: ${window.location.origin}/empresa/login\nE-mail: ${credentials.email}\nSenha: ${credentials.password}\n\nRecomendamos trocar a senha no primeiro acesso.`;
                  copyToClipboard(msg, "Mensagem completa");
                }}
              >
                <Copy className="w-4 h-4 mr-2" /> Copiar mensagem completa
              </Button>
            </div>
          )}
          <p className="text-xs text-muted-foreground text-center">
            {isExisting
              ? "O parceiro já possui conta e pode acessar com suas credenciais existentes."
              : "O parceiro também receberá um e-mail de confirmação do sistema."}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPedidosParceria() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [decisionReason, setDecisionReason] = useState("");
  const { orgId } = useAdminOrgContext();
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  const [showCredentials, setShowCredentials] = useState(false);

  const { data: requests, isLoading } = useQuery({
    queryKey: ["admin_partner_requests"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partner_requests")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });

  // Approve via edge function (creates account automatically)
  const approveMutation = useMutation({
    mutationFn: async (req: any) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Sessão expirada");

      const res = await supabase.functions.invoke("create-partner-account", {
        body: {
          requestId: req.id,
          companyName: req.company_name,
          contactEmail: req.contact_email,
          contactName: req.contact_name,
          industry: null,
        },
      });

      // On non-2xx, supabase puts the parsed body in res.data and a generic error in res.error
      if (res.error) {
        const errorMsg = res.data?.error || res.error.message || "Erro ao criar conta";
        throw new Error(errorMsg);
      }
      const body = res.data;
      if (!body.success) throw new Error(body.error || "Erro ao criar conta");
      return body;
    },
    onSuccess: (data, req) => {
      queryClient.invalidateQueries({ queryKey: ["admin_partner_requests"] });
      setCredentials(data.credentials);
      setShowCredentials(true);
      setExpandedId(null);
      setDecisionReason("");
      if (orgId) {
        logAudit({
          orgId,
          action: "approve_partner_request",
          entityType: "partner_request",
          entityId: req.id,
        });
      }
    },
    onError: (err: any) => {
      toast({ title: "Erro ao aprovar", description: err.message, variant: "destructive" });
    },
  });

  // Reject (simple update)
  const rejectMutation = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const { error } = await supabase
        .from("partner_requests")
        .update({
          status: "rejected",
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString(),
          decision_reason: decisionReason.trim() || null,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["admin_partner_requests"] });
      toast({ title: "Solicitação rejeitada" });
      if (orgId) {
        logAudit({ orgId, action: "reject_partner_request", entityType: "partner_request", entityId: id });
      }
      setExpandedId(null);
      setDecisionReason("");
    },
    onError: (err: any) => {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    },
  });

  const pending = (requests ?? []).filter((r) => r.status === "pending");
  const resolved = (requests ?? []).filter((r) => r.status !== "pending");
  const isMutating = approveMutation.isPending || rejectMutation.isPending;

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl">
      <CredentialsDialog credentials={credentials} open={showCredentials} onClose={() => setShowCredentials(false)} />

      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-heading text-2xl font-bold text-foreground">Pedidos de Parceria</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {pending.length} pendente{pending.length !== 1 ? "s" : ""} · {resolved.length} resolvido{resolved.length !== 1 ? "s" : ""}
        </p>
      </motion.div>

      <div className="mt-6 space-y-3">
        {pending.length === 0 && resolved.length === 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-muted/50 rounded-2xl p-12 text-center">
            <Handshake className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-foreground mb-2">Nenhum pedido ainda</h3>
            <p className="text-sm text-muted-foreground">Quando empresas solicitarem parceria, os pedidos aparecerão aqui.</p>
          </motion.div>
        )}

        {pending.map((req, i) => (
          <motion.div
            key={req.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 * i }}
            className="bg-card rounded-2xl p-5 border border-border"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-yellow-700" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-foreground truncate">{req.company_name}</h3>
                  <p className="text-sm text-muted-foreground">{req.contact_name} · {req.contact_email}</p>
                  {req.industry && <p className="text-xs text-muted-foreground mt-0.5">Segmento: {req.industry}</p>}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(Array.isArray(req.partner_types) ? req.partner_types : []).map((t: string) => (
                      <Badge key={t} variant="outline" className="text-xs">
                        {PARTNER_TYPE_LABELS[t] ?? t}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
              <Badge className={STATUS_MAP.pending.className}>{STATUS_MAP.pending.label}</Badge>
            </div>

            {expandedId === req.id ? (
              <div className="mt-4 space-y-3 border-t border-border pt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  {req.contact_phone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="w-3.5 h-3.5" /> {req.contact_phone}
                    </div>
                  )}
                  {req.website && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <ExternalLink className="w-3.5 h-3.5" />
                      <a href={req.website} target="_blank" rel="noopener noreferrer" className="underline truncate">{req.website}</a>
                    </div>
                  )}
                  {(req.city || req.state) && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="w-3.5 h-3.5" /> {[req.city, req.state].filter(Boolean).join(", ")}
                    </div>
                  )}
                </div>
                {req.message && (
                  <div className="bg-muted/50 rounded-xl p-3 text-sm text-foreground">{req.message}</div>
                )}
                <div className="space-y-2">
                  <Textarea
                    placeholder="Motivo da decisão (opcional)"
                    value={decisionReason}
                    onChange={(e) => setDecisionReason(e.target.value)}
                    rows={2}
                    maxLength={500}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="flex-1"
                      onClick={() => approveMutation.mutate(req)}
                      disabled={isMutating}
                    >
                      {approveMutation.isPending ? (
                        <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Criando conta...</>
                      ) : (
                        <><CheckCircle2 className="w-4 h-4 mr-1" /> Aprovar e criar conta</>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="flex-1"
                      onClick={() => rejectMutation.mutate({ id: req.id })}
                      disabled={isMutating}
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Rejeitar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setExpandedId(null); setDecisionReason(""); }}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <Button variant="ghost" size="sm" className="mt-3" onClick={() => setExpandedId(req.id)}>
                Ver detalhes e decidir
              </Button>
            )}
          </motion.div>
        ))}
      </div>

      {resolved.length > 0 && (
        <div className="mt-8">
          <h2 className="font-heading text-lg font-semibold text-foreground mb-3">Histórico</h2>
          <div className="space-y-2">
            {resolved.map((req) => {
              const st = STATUS_MAP[req.status] ?? { label: req.status, className: "bg-muted text-muted-foreground" };
              return (
                <div key={req.id} className="bg-card rounded-xl p-4 border border-border flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">{req.company_name}</p>
                    <p className="text-xs text-muted-foreground">{req.contact_name} · {new Date(req.created_at).toLocaleDateString("pt-BR")}</p>
                    {req.decision_reason && (
                      <p className="text-xs text-muted-foreground mt-1 italic">"{req.decision_reason}"</p>
                    )}
                  </div>
                  <Badge className={st.className}>{st.label}</Badge>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
